import clsx from 'clsx';
import React from 'react';

import { TextButton } from '@components/Button/variants';
import BylineWithHeadshot from '@components/BylineWithHeadshot';
import ClientRenderedContent from '@components/ClientRenderedContent';
import IconLink from '@components/Images/Rebranding/IconLink';
import useEventTracking from '@hooks/useEventTracking';
import { Author } from '@interfaces/authors';
import { ClickTarget, click } from '@tracking';

import { BriefingSource } from './index';

interface BriefingDetailProps {
  authors: Author[];
  canViewFullBriefing: boolean;
  publishedAt: string;
  source: BriefingSource;
}

const BriefingDetails = ({
  authors,
  publishedAt,
  canViewFullBriefing,
  source,
}: BriefingDetailProps) => {
  const { trackEvent } = useEventTracking();

  return (
    <div
      className={clsx('briefing-detail flex max-w-[350px] flex-col', {
        'pointer-events-none opacity-60': !canViewFullBriefing,
      })}
    >
      <BylineWithHeadshot
        authors={authors}
        authorClassName="author-name-body"
        className="max-w-sm"
      />
      <div className="border-grayscale-gra w-full border-0 border-b border-solid border-grayscale-gray pt-24" />
      <div
        className={clsx(
          'mt-8 flex flex-wrap items-center justify-between gap-24 text-grayscale-gray lg:items-start',
          { 'opacity-50': !canViewFullBriefing }
        )}
      >
        <ClientRenderedContent>
          <time
            className="block font-suisse-intl-mono text-overline-md text-grayscale-gray lg:text-overline-sm xl:text-overline-md"
            dateTime={publishedAt}
          >
            {publishedAt}
          </time>
        </ClientRenderedContent>
        <div className="mr-16 flex items-center">
          <IconLink className="h-16 w-16  text-grayscale-gray" />
          <span className="px-8 font-suisse-intl-mono text-overline-sm text-grayscale-gray">
            Source:
          </span>
          <TextButton
            as="link"
            href={source.url}
            className={clsx(
              '!w-3/5 font-suisse-intl text-icon-sm text-grayscale-gray no-underline',
              { 'opacity-50': !canViewFullBriefing }
            )}
            onClickCapture={() =>
              trackEvent(
                click({
                  clickLink: source.url,
                  clickTarget: ClickTarget.BriefingSource,
                  clickText: source.name,
                })
              )
            }
          >
            {source.name}
          </TextButton>
        </div>
      </div>
    </div>
  );
};

export default BriefingDetails;
